import React from 'react';
import styled, { withTheme } from 'styled-components';
import mermaidImport from 'mermaid';
import DOMPurify from 'dompurify';
import Flex from './Flex';
import Text from './Text';
import Spinner from './Spinner';
import Callout from './Callout';
import Dialog from './Dialog';
import Button from './Button';
import Tooltip from './Tooltip';
import Popover from './Popover';
import Menu from './Menu';
import MenuItem from './MenuItem';

// Resolve mermaid default export: handles both webpack (direct default) and
// jest/babel interop where ESM modules get double-wrapped as { default: ... }.
const mermaid = mermaidImport.default || mermaidImport;

const MermaidContainer = styled.div`
  position: relative;

  .mermaid {
    display: flex;
    justify-content: center;

    svg {
      max-width: 100%;
      height: auto;
    }
  }
`;

const ZoomButton = styled(Button)`
  margin-right: 8px;
  z-index: 1;
`;

const DialogContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;

  .mermaid {
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;

    svg {
      max-width: 100%;
      max-height: 100%;
      height: auto;
      width: auto;
    }
  }
`;

/**
 * MermaidDiagram - Renders mermaid diagram syntax as SVG
 *
 * @param {string} chart - The mermaid diagram definition
 * @param {object} theme - Theme object from styled-components (injected by withTheme)
 *
 * Example usage:
 * <MermaidDiagram chart="graph TD; A-->B; A-->C;" />
 */
class MermaidDiagram extends React.Component {
  state = {
    svg: null,
    error: null,
    isZoomDialogOpen: false
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
  }

  async componentDidMount() {
    this.initializeMermaid();
    await this.renderDiagram();
  }

  async componentDidUpdate(prevProps) {
    const { chart, theme } = this.props;

    // Reinitialize if theme changes
    if (prevProps.theme !== theme) {
      this.initializeMermaid();
    }

    if (prevProps.chart !== chart || prevProps.theme !== theme) {
      await this.renderDiagram();
    }
  }

  initializeMermaid() {
    const { theme } = this.props;

    // Use 'base' theme with themeVariables to map mermaid colors from our app theme.
    // Docs: https://mermaid.js.org/config/theming.html
    mermaid.initialize({
      startOnLoad: false,
      theme: 'base',
      fontFamily: theme.fonts.body,
      securityLevel: 'strict',
      themeVariables: {
        background: theme.colors.appBackground,
        primaryColor: theme.colors.cardBackground,
        primaryTextColor: theme.colors.body,
        primaryBorderColor: theme.colors.muted,
        secondaryColor: theme.backgrounds.tableHeader,
        secondaryTextColor: theme.colors.body,
        secondaryBorderColor: theme.borderColors.thin,
        tertiaryColor: theme.backgrounds.app,
        tertiaryTextColor: theme.colors.body,
        tertiaryBorderColor: theme.borderColors.thin,
        nodeBorder: theme.colors.muted,
        nodeTextColor: theme.colors.body,
        lineColor: theme.colors.muted,
        edgeLabelBackground: theme.backgrounds.tableHeader,
        textColor: theme.colors.body,
        mainBkg: theme.colors.cardBackground,
        clusterBkg: theme.backgrounds.app,
        clusterBorder: theme.borderColors.thin,
        titleColor: theme.colors.body,
        noteBkgColor: theme.colors.cardBackground,
        noteTextColor: theme.colors.body,
        noteBorderColor: theme.borderColors.thin
      }
    });
  }

  static containsDangerousScheme(text = '') {
    return /\b(?:javascript|data|vbscript)\s*:/i.test(String(text));
  }

  static sanitizeSvg(svg) {
    return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true }, ADD_TAGS: ['foreignObject'] });
  }

  async renderDiagram() {
    try {
      const { chart } = this.props;

      if (MermaidDiagram.containsDangerousScheme(chart)) {
        this.setState({ error: 'Diagram contains disallowed content', svg: null });
        return;
      }

      // Replace literal \n (backslash + n) with <br/> for line breaks in node labels.
      // LLMs generate \n in mermaid labels but mermaid renders them verbatim;
      // <br/> is the supported way to express newlines inside mermaid labels.
      const processedChart = chart.replace(/\\n/g, '<br/>');
      const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
      const { svg } = await mermaid.render(id, processedChart);
      this.setState({ svg: MermaidDiagram.sanitizeSvg(svg), error: null });
    } catch (error) {
      console.error('Mermaid rendering error:', error);
      this.setState({ error: error.message, svg: null });
    }
  }

  handleZoomClick = () => {
    this.setState({ isZoomDialogOpen: true });
  };

  handleCloseDialog = () => {
    this.setState({ isZoomDialogOpen: false });
  };

  handleSaveAsSVG = () => {
    const { svg } = this.state;
    if (!svg) {
      return;
    }

    // Create a blob from the SVG content
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `mermaid-diagram-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleSaveAsMermaid = () => {
    const { chart } = this.props;
    if (!chart) {
      return;
    }

    // Create a blob from the Mermaid text
    const blob = new Blob([chart], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `mermaid-diagram-${Date.now()}.mmd`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  renderExportMenu = () => (
    <Menu>
      <MenuItem text="Save as SVG" label=".svg" onClick={this.handleSaveAsSVG} />
      <MenuItem text="Save as Mermaid" label=".mmd" onClick={this.handleSaveAsMermaid} />
    </Menu>
  );

  render() {
    const { svg, error, isZoomDialogOpen } = this.state;

    if (error) {
      return (
        <Callout intent="danger" title="Error rendering diagram">
          {error}
        </Callout>
      );
    }

    if (!svg) {
      return (
        <Flex alignItems="center" gap="4px" my={1}>
          <Spinner size={12} />
          <Text muted small>
            Loading diagram...
          </Text>
        </Flex>
      );
    }

    /* eslint-disable react/no-danger */
    return (
      <>
        <MermaidContainer>
          <Flex justifyContent="flex-end" mb={1}>
            <Tooltip content="Zoom" position="top">
              <ZoomButton icon="zoom-in" small onClick={this.handleZoomClick} title="Zoom" />
            </Tooltip>
            <Popover position="bottom-right">
              <Tooltip content="Export" position="top">
                <Button icon="export" small title="Export" />
              </Tooltip>
              {this.renderExportMenu()}
            </Popover>
          </Flex>
          <div className="mermaid" ref={this.containerRef} dangerouslySetInnerHTML={{ __html: svg }} />
        </MermaidContainer>

        <Dialog
          isOpen={isZoomDialogOpen}
          onClose={this.handleCloseDialog}
          title="Mermaid Diagram"
          style={{ width: '90vw', height: '90vh' }}
        >
          <DialogContent>
            <div className="mermaid" dangerouslySetInnerHTML={{ __html: svg }} />
          </DialogContent>
          <Dialog.Footer>
            <Flex justifyContent="flex-end" width="100%" gap={1}>
              <Popover position="top">
                <Button icon="export" text="Save" />
                {this.renderExportMenu()}
              </Popover>
              <Button onClick={this.handleCloseDialog} text="Close" />
            </Flex>
          </Dialog.Footer>
        </Dialog>
      </>
    );
    /* eslint-enable react/no-danger */
  }
}

export default withTheme(MermaidDiagram);
